import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, X, FileImage, FileVideo, FileAudio, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_SIZE_LIMITS = {
  image: 5 * 1024 * 1024,      // 5MB
  video: 16 * 1024 * 1024,     // 16MB
  audio: 16 * 1024 * 1024,     // 16MB
  document: 100 * 1024 * 1024  // 100MB
};

const ALLOWED_EXTENSIONS = {
  image: ['.jpg', '.jpeg', '.png', '.webp'],
  video: ['.mp4', '.3gp'],
  audio: ['.mp3', '.ogg', '.aac', '.wav', '.mpeg'],
  document: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv']
};

const MediaUploader = ({ mediaType, onUploadComplete, onUploadStart, onUploadEnd, label = "Upload File", accept, showPreview = true }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState('');

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'image': return <FileImage className="w-5 h-5" />;
      case 'video': return <FileVideo className="w-5 h-5" />;
      case 'audio': return <FileAudio className="w-5 h-5" />;
      case 'document': return <FileText className="w-5 h-5" />;
      default: return <Upload className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    const maxSize = MEDIA_SIZE_LIMITS[mediaType];
    const allowedExts = ALLOWED_EXTENSIONS[mediaType];

    if (!allowedExts.includes(fileExt)) {
      throw new Error(`Format file tidak didukung. Format yang diizinkan: ${allowedExts.join(', ')}`);
    }

    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      throw new Error(`Ukuran file melebihi batas maksimum ${maxSizeMB}MB`);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      validateFile(file);
      setFileName(file.name);

      // Show preview for images
      if (mediaType === 'image' && showPreview) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }

      setUploading(true);
      if (onUploadStart) onUploadStart();

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/media/upload?media_type=${mediaType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`File berhasil diupload (${formatFileSize(response.data.size)})`);
      
      if (onUploadComplete) {
        onUploadComplete(response.data.url);
      }

    } catch (error) {
      console.error('Upload error:', error);
      if (error.message) {
        toast.error(error.message);
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Upload gagal');
      }
      // Reset
      setPreview(null);
      setFileName('');
    } finally {
      setUploading(false);
      if (onUploadEnd) onUploadEnd();
    }
  };

  const clearUpload = () => {
    setPreview(null);
    setFileName('');
    if (onUploadComplete) {
      onUploadComplete('');
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {!fileName ? (
        <div className="relative">
          <Input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading}
            className="cursor-pointer"
          />
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
              <span className="text-sm">Mengupload...</span>
            </div>
          )}
        </div>
      ) : (
        <div className="border rounded-lg p-3 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            {getMediaIcon()}
            <span className="text-sm font-medium truncate max-w-[200px]">{fileName}</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearUpload}
            data-testid="clear-upload-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      {preview && showPreview && (
        <div className="mt-2 border rounded-lg overflow-hidden">
          <img src={preview} alt="Preview" className="w-full h-48 object-cover" />
        </div>
      )}
      <p className="text-xs text-gray-500">
        Maks {formatFileSize(MEDIA_SIZE_LIMITS[mediaType])} | Format: {ALLOWED_EXTENSIONS[mediaType].join(', ')}
      </p>
    </div>
  );
};

export default MediaUploader;
